<script setup lang="ts">
/**
 * AssistantToolCard (F1, Investment-4 Wave-1) — render ONE assistant tool call
 * as a typed ACTION-CARD instead of raw JSON.
 *
 * Per-tool layouts:
 *   draft_segment        → audience + confidence + explanation + [Создать сегмент]
 *                          / [Рассылка на сегмент] (write actions, RBAC-gated)
 *   get_campaign_stats   → KPI grid for one broadcast
 *   compare_periods      → A vs B value + delta%
 *   list_recent_broadcasts → list of broadcasts with [Открыть]
 *   <unknown tool>       → graceful fallback to raw JSON (forward-compat)
 *
 * The raw input/output JSON is ALWAYS available in a collapsible <details> so
 * nothing is hidden from the operator.
 *
 * AI never writes silently: write buttons call existing protected endpoints by
 * human click only, and are hidden when the user lacks the role (canWrite).
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  Users, BarChart3, GitCompareArrows, Radio, Code2, Plus, Send, ExternalLink,
  AlertTriangle, ChevronDown, ChevronRight,
  Gift, GitBranch, ClipboardList, CheckCircle2,
  Megaphone, MousePointerClick, FileSearch,
  ListChecks, Contact, Target, BookOpen,
} from "lucide-vue-next";
import {
  readDraftSegment, readRecommendOffer, readJourneyDraft, readSurveyDraft,
  readDraftBroadcast, readExperienceDraft, readExplainDecision,
  readSurveyList, readClientList, readSegmentOffers,
  type JourneyDraftPayload, type SurveyDraftPayload, type ExperienceDraftPayload,
} from "./assistant-card-model";

const { t } = useI18n();

interface ToolCall {
  tool: string;
  input: Record<string, unknown>;
  output?: unknown;
}

const props = defineProps<{
  call: ToolCall;
  /** Whether the current user may perform write actions (tenant_admin+). */
  canWrite: boolean;
  /** Whether the raw JSON <details> is open. */
  rawOpen: boolean;
}>();

const emit = defineEmits<{
  (e: "toggle-raw"): void;
  (e: "create-segment", payload: { filter: Record<string, unknown>; suggestedName: string }): void;
  (e: "broadcast-on-segment", payload: { filter: Record<string, unknown>; suggestedName: string }): void;
  (e: "open-broadcast", id: string): void;
  // D3 — Copilot proposal cards. The card NEVER writes; it emits and the page
  // opens a confirm modal before calling the protected route (approve step).
  (e: "open-offer", id: string): void;
  (e: "create-journey", payload: { draft: JourneyDraftPayload }): void;
  (e: "publish-survey", payload: { draft: SurveyDraftPayload }): void;
  // Copilot Apply wave — draft → human opens the prefilled editor (no write here).
  (e: "draft-broadcast", payload: { filter: Record<string, unknown>; suggestedName: string; bodyText: string }): void;
  (e: "create-experience", payload: { draft: ExperienceDraftPayload }): void;
  // Phase-1 READ tools — optional «Открыть» navigation (no write).
  (e: "open-survey", id: string): void;
  (e: "open-profile", id: string): void;
}>();

const out = computed<Record<string, unknown>>(() =>
  props.call.output && typeof props.call.output === "object" && !Array.isArray(props.call.output)
    ? (props.call.output as Record<string, unknown>)
    : {},
);

// Generic top-level error banner. The Copilot Apply tools render their own
// error UX (draft_broadcast still shows name+body on NL failure; explain_decision
// is a plain string), so they opt out of the generic banner here.
const OWN_ERROR_UX = new Set(["draft_broadcast", "create_experience", "explain_decision"]);
const hasError = computed(
  () => typeof out.value.error === "string" && !OWN_ERROR_UX.has(props.call.tool),
);
// Prefer the human-readable `message` (engine in-band errors carry both, e.g.
// {error:"forbidden", message:"Недостаточно прав…"}); fall back to the code.
const errorText = computed(() =>
  typeof out.value.message === "string" && out.value.message
    ? (out.value.message as string)
    : (out.value.error as string),
);

// ─── draft_segment (delegates to the pure card model) ───
const seg = computed(() => readDraftSegment(props.call.output));
const segmentFilter = computed(() => seg.value.filter);
const segmentAudience = computed(() => seg.value.audienceSize);
const segmentConfidence = computed(() => seg.value.confidence);
const segmentExplanation = computed(() => seg.value.explanation);
const suggestedSegmentName = computed(() => seg.value.suggestedName);
const confidenceClass = computed(() => {
  switch (segmentConfidence.value) {
    case "high":   return "bg-success-500/10 text-success-600 ring-1 ring-success-500/30";
    case "medium": return "bg-warning-500/10 text-warning-600 ring-1 ring-warning-500/30";
    default:       return "bg-danger-500/10  text-danger-600  ring-1 ring-danger-500/30";
  }
});

// ─── get_campaign_stats ───
const stats = computed<Record<string, unknown> | null>(() => {
  const s = out.value.stats;
  return s && typeof s === "object" ? (s as Record<string, unknown>) : null;
});
const broadcastMeta = computed<Record<string, unknown> | null>(() => {
  const b = out.value.broadcast;
  return b && typeof b === "object" ? (b as Record<string, unknown>) : null;
});
function pct(v: unknown): string {
  return typeof v === "number" ? `${(v * 100).toFixed(1)}%` : "—";
}
function num(v: unknown): string {
  return typeof v === "number" ? v.toLocaleString("ru-RU") : "—";
}

// ─── compare_periods ───
const compareA = computed<Record<string, unknown> | null>(() => {
  const a = out.value.period_a;
  return a && typeof a === "object" ? (a as Record<string, unknown>) : null;
});
const compareB = computed<Record<string, unknown> | null>(() => {
  const b = out.value.period_b;
  return b && typeof b === "object" ? (b as Record<string, unknown>) : null;
});
const deltaPct = computed<number | null>(() =>
  typeof out.value.delta_pct === "number" ? out.value.delta_pct : null,
);

// ─── list_recent_broadcasts ───
const broadcastRows = computed<Array<Record<string, unknown>>>(() => {
  const rows = out.value.rows;
  return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
});

// ─── D3: recommend_offer (read-only recommendation) ───
const offerView = computed(() => readRecommendOffer(props.call.output));

// ─── D3: create_journey (proposal) ───
const journeyView = computed(() => readJourneyDraft(props.call.output));

// ─── D3: publish_survey (proposal) ───
const surveyView = computed(() => readSurveyDraft(props.call.output));

// ─── Copilot Apply: draft_broadcast (proposal) ───
const broadcastDraft = computed(() => readDraftBroadcast(props.call.output));

// ─── Copilot Apply: create_experience (proposal) ───
const experienceDraft = computed(() => readExperienceDraft(props.call.output));

// ─── Copilot Apply: explain_decision (read / chat text) ───
const explainView = computed(() => readExplainDecision(props.call.output));

// Phase-1 READ: list_surveys / list_clients / recommend_offer_by_segment.
const surveyListView = computed(() => readSurveyList(props.call.output));
const clientListView = computed(() => readClientList(props.call.output));
const segmentOffersView = computed(() => readSegmentOffers(props.call.output));

/** Pill styling for a survey status (active = success, archived = muted). */
function surveyStatusClass(status: string): string {
  if (status === "active") return "bg-success-500/10 text-success-600 ring-1 ring-success-500/30";
  if (status === "archived") return "bg-surface-100 text-surface-500 ring-1 ring-surface-200";
  return "bg-warning-500/10 text-warning-600 ring-1 ring-warning-500/30";
}
function surveyStatusLabel(status: string): string {
  if (status === "active") return t("ai_cards.list_surveys.status_active");
  if (status === "archived") return t("ai_cards.list_surveys.status_archived");
  return t("ai_cards.list_surveys.status_inactive");
}

/** Map a confidence level to the same pill styling used by the segment card. */
function confClass(level: "high" | "medium" | "low"): string {
  switch (level) {
    case "high":   return "bg-success-500/10 text-success-600 ring-1 ring-success-500/30";
    case "medium": return "bg-warning-500/10 text-warning-600 ring-1 ring-warning-500/30";
    default:       return "bg-danger-500/10  text-danger-600  ring-1 ring-danger-500/30";
  }
}
function confLabel(level: "high" | "medium" | "low"): string {
  return level === "high" ? "высокая" : level === "medium" ? "средняя" : "низкая";
}

/** Render a 0..1 fraction as a whole-percent string («72 %»), clamped to [0..100]. */
function fracPct(v: number | null): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return `${Math.max(0, Math.min(100, Math.round(v * 100)))} %`;
}

function jsonPretty(v: unknown): string {
  if (v === null || v === undefined) return "—";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

const titleIcon = computed(() => {
  switch (props.call.tool) {
    case "draft_segment":          return Users;
    case "get_campaign_stats":     return BarChart3;
    case "compare_periods":        return GitCompareArrows;
    case "list_recent_broadcasts": return Radio;
    case "recommend_offer":        return Gift;
    case "create_journey":         return GitBranch;
    case "publish_survey":         return ClipboardList;
    case "draft_broadcast":        return Megaphone;
    case "create_experience":      return MousePointerClick;
    case "explain_decision":       return FileSearch;
    case "list_surveys":           return ListChecks;
    case "list_clients":           return Contact;
    case "recommend_offer_by_segment": return Target;
    case "search_docs":            return BookOpen;
    default:                       return Code2;
  }
});
const titleLabel = computed(() => {
  switch (props.call.tool) {
    case "draft_segment":          return "Черновик сегмента";
    case "get_campaign_stats":     return "Статистика рассылки";
    case "compare_periods":        return "Сравнение периодов";
    case "list_recent_broadcasts": return "Последние рассылки";
    case "recommend_offer":        return t("ai_cards.recommend_offer.title");
    case "create_journey":         return t("ai_cards.create_journey.title");
    case "publish_survey":         return t("ai_cards.publish_survey.title");
    case "draft_broadcast":        return t("ai_cards.draft_broadcast.title");
    case "create_experience":      return t("ai_cards.create_experience.title");
    case "explain_decision":       return t("ai_cards.explain_decision.title");
    case "list_surveys":           return t("ai_cards.list_surveys.title");
    case "list_clients":           return t("ai_cards.list_clients.title");
    case "recommend_offer_by_segment": return t("ai_cards.recommend_offer_by_segment.title");
    case "search_docs":            return t("ai_cards.search_docs.title");
    default:                       return props.call.tool;
  }
});
</script>

<template>
  <div class="border border-surface-200 rounded-lg bg-surface-0 overflow-hidden">
    <!-- Card header -->
    <div class="flex items-center gap-2 px-3 py-2 bg-surface-50 border-b border-surface-200">
      <component :is="titleIcon" class="size-4 text-accent-600" />
      <span class="text-sm font-medium text-surface-800">{{ titleLabel }}</span>
    </div>

    <div class="p-3 text-sm">
      <!-- Error from the tool -->
      <div v-if="hasError" class="text-danger-600 flex items-start gap-2">
        <AlertTriangle class="size-4 mt-0.5 shrink-0" />
        <span>{{ errorText }}</span>
      </div>

      <!-- draft_segment -->
      <template v-else-if="call.tool === 'draft_segment'">
        <div v-if="out.unavailable" class="text-surface-500">{{ out.message }}</div>
        <template v-else>
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-baseline gap-2">
              <Users class="size-5 text-accent-600" />
              <span class="text-2xl font-bold text-surface-900">{{ segmentAudience ?? "—" }}</span>
              <span class="text-xs text-surface-500">профилей</span>
            </div>
            <span class="text-xs px-2 py-0.5 rounded-full font-medium" :class="confidenceClass">
              {{ segmentConfidence === 'high' ? 'высокая' : segmentConfidence === 'medium' ? 'средняя' : 'низкая' }}
            </span>
          </div>
          <p v-if="segmentExplanation" class="text-surface-700 mb-3">{{ segmentExplanation }}</p>

          <div v-if="segmentFilter && canWrite" class="flex flex-wrap items-center gap-2">
            <button
              class="btn-primary text-sm inline-flex items-center gap-1.5"
              @click="emit('create-segment', { filter: segmentFilter!, suggestedName: suggestedSegmentName })"
            >
              <Plus class="size-4" /> Создать сегмент
            </button>
            <button
              class="btn-secondary text-sm inline-flex items-center gap-1.5"
              @click="emit('broadcast-on-segment', { filter: segmentFilter!, suggestedName: suggestedSegmentName })"
            >
              <Send class="size-4" /> Рассылка на сегмент
            </button>
          </div>
          <p v-else-if="segmentFilter && !canWrite" class="text-xs text-surface-400">
            Создание сегмента доступно администратору организации.
          </p>
          <p v-else class="text-xs text-warning-600 flex items-center gap-1">
            <AlertTriangle class="size-3.5" /> Не удалось построить фильтр — уточните запрос.
          </p>
        </template>
      </template>

      <!-- get_campaign_stats -->
      <template v-else-if="call.tool === 'get_campaign_stats' && stats">
        <div v-if="broadcastMeta" class="mb-2">
          <button
            v-if="canWrite || true"
            class="text-accent-700 hover:underline inline-flex items-center gap-1 font-medium"
            @click="emit('open-broadcast', String(broadcastMeta!.id))"
          >
            {{ broadcastMeta.name }} <ExternalLink class="size-3.5" />
          </button>
          <span class="text-xs text-surface-400 ml-2">{{ broadcastMeta.status }}</span>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div class="bg-surface-50 rounded p-2">
            <div class="text-xs text-surface-500">Отправлено</div>
            <div class="font-semibold text-surface-900">{{ num(stats.sent) }}</div>
          </div>
          <div class="bg-surface-50 rounded p-2">
            <div class="text-xs text-surface-500">Доставлено</div>
            <div class="font-semibold text-surface-900">{{ num(stats.delivered) }}</div>
          </div>
          <div class="bg-surface-50 rounded p-2">
            <div class="text-xs text-surface-500">Открыли</div>
            <div class="font-semibold text-surface-900">{{ num(stats.opened) }} · {{ pct(stats.open_rate) }}</div>
          </div>
          <div class="bg-surface-50 rounded p-2">
            <div class="text-xs text-surface-500">Клики</div>
            <div class="font-semibold text-surface-900">{{ num(stats.clicked) }} · {{ pct(stats.click_rate) }}</div>
          </div>
        </div>
      </template>

      <!-- compare_periods -->
      <template v-else-if="call.tool === 'compare_periods' && compareA && compareB">
        <div class="flex items-center gap-3">
          <div class="bg-surface-50 rounded p-2 flex-1">
            <div class="text-xs text-surface-500">Период A</div>
            <div class="font-semibold text-surface-900">{{ num(compareA.value) }}</div>
          </div>
          <div class="bg-surface-50 rounded p-2 flex-1">
            <div class="text-xs text-surface-500">Период B</div>
            <div class="font-semibold text-surface-900">{{ num(compareB.value) }}</div>
          </div>
          <div v-if="deltaPct !== null" class="text-center px-2">
            <div class="text-xs text-surface-500">Изменение</div>
            <div
              class="font-semibold"
              :class="deltaPct >= 0 ? 'text-success-600' : 'text-danger-600'"
            >
              {{ deltaPct >= 0 ? '+' : '' }}{{ deltaPct.toFixed(1) }}%
            </div>
          </div>
        </div>
        <div class="text-xs text-surface-400 mt-1">Метрика: {{ out.metric }}</div>
      </template>

      <!-- list_recent_broadcasts -->
      <template v-else-if="call.tool === 'list_recent_broadcasts'">
        <ul v-if="broadcastRows.length" class="divide-y divide-surface-100">
          <li v-for="(b, i) in broadcastRows" :key="i" class="py-1.5 flex items-center justify-between gap-2">
            <button
              class="text-left text-accent-700 hover:underline inline-flex items-center gap-1 truncate"
              @click="emit('open-broadcast', String(b.id))"
            >
              <span class="truncate">{{ b.name }}</span>
              <ExternalLink class="size-3 shrink-0" />
            </button>
            <span class="text-xs text-surface-400 shrink-0">{{ b.status }}</span>
          </li>
        </ul>
        <p v-else class="text-surface-400 text-xs">Рассылок не найдено.</p>
      </template>

      <!-- D3: recommend_offer (READ-only recommendation — never an apply button) -->
      <template v-else-if="call.tool === 'recommend_offer'">
        <template v-if="offerView.offer">
          <div class="flex items-start justify-between gap-2 mb-1.5">
            <div class="flex items-center gap-2 min-w-0">
              <Gift class="size-5 text-accent-600 shrink-0" />
              <span class="font-semibold text-surface-900 truncate">{{ offerView.offer.name }}</span>
              <span
                v-if="offerView.offer.kind"
                class="text-[11px] px-1.5 py-0.5 rounded-full bg-accent-500/10 text-accent-600 ring-1 ring-accent-500/30 shrink-0"
              >{{ offerView.offer.kind }}</span>
            </div>
            <span
              v-if="offerView.score !== null"
              class="text-xs px-2 py-0.5 rounded-full font-medium bg-success-500/10 text-success-600 ring-1 ring-success-500/30 shrink-0"
              :title="t('ai_cards.recommend_offer.score')"
            >{{ fracPct(offerView.score) }}</span>
          </div>
          <p v-if="offerView.offer.description" class="text-surface-700 mb-2">{{ offerView.offer.description }}</p>
          <p v-if="offerView.reason" class="text-surface-600 text-xs mb-2">
            <span class="font-medium text-surface-500">{{ t("ai_cards.recommend_offer.reason") }}:</span>
            {{ offerView.reason }}
          </p>
          <div v-if="offerView.reasonCodes.length" class="flex flex-wrap gap-1 mb-2">
            <span
              v-for="(rc, ri) in offerView.reasonCodes"
              :key="ri"
              class="text-[11px] px-1.5 py-0.5 rounded bg-surface-100 text-surface-500"
            >{{ rc }}</span>
          </div>
          <button
            class="text-accent-700 hover:underline inline-flex items-center gap-1 text-sm"
            @click="emit('open-offer', offerView.offer.id)"
          >
            {{ t("ai_cards.recommend_offer.open") }} <ExternalLink class="size-3.5" />
          </button>
        </template>
        <p v-else class="text-surface-500">{{ t("ai_cards.recommend_offer.none") }}</p>
      </template>

      <!-- D3: create_journey (PROPOSAL — human «Применить» persists via POST /v1/journeys) -->
      <template v-else-if="call.tool === 'create_journey'">
        <template v-if="journeyView.draft">
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-center gap-2 min-w-0">
              <GitBranch class="size-5 text-accent-600 shrink-0" />
              <span class="font-semibold text-surface-900 truncate">{{ journeyView.draft.name }}</span>
            </div>
            <span
              v-if="journeyView.confidence !== null"
              class="text-xs px-2 py-0.5 rounded-full font-medium bg-accent-500/10 text-accent-600 ring-1 ring-accent-500/30 shrink-0"
              :title="t('ai_cards.create_journey.confidence')"
            >{{ fracPct(journeyView.confidence) }}</span>
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-surface-600 mb-2">
            <span v-if="journeyView.draft.entry_event_type">
              <span class="text-surface-400">{{ t("ai_cards.create_journey.entry") }}:</span>
              <code class="ml-1 text-surface-700">{{ journeyView.draft.entry_event_type }}</code>
            </span>
            <span>
              <span class="text-surface-400">{{ t("ai_cards.create_journey.steps") }}:</span>
              <span class="ml-1 font-medium text-surface-800">{{ journeyView.stepCount }}</span>
            </span>
          </div>
          <p v-if="journeyView.explanation" class="text-surface-700 mb-3">{{ journeyView.explanation }}</p>

          <button
            v-if="canWrite"
            class="btn-primary text-sm inline-flex items-center gap-1.5"
            @click="emit('create-journey', { draft: journeyView.draft! })"
          >
            <Plus class="size-4" /> {{ t("ai_cards.create_journey.apply") }}
          </button>
          <p v-else class="text-xs text-surface-400">{{ t("ai_cards.create_journey.no_access") }}</p>
        </template>
        <p v-else class="text-xs text-warning-600 flex items-center gap-1">
          <AlertTriangle class="size-3.5" /> {{ t("ai_cards.create_journey.invalid") }}
        </p>
      </template>

      <!-- D3: publish_survey (PROPOSAL — human «Применить» creates/publishes the survey) -->
      <template v-else-if="call.tool === 'publish_survey'">
        <template v-if="surveyView.draft">
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-center gap-2 min-w-0">
              <ClipboardList class="size-5 text-accent-600 shrink-0" />
              <span class="font-semibold text-surface-900 truncate">{{ surveyView.draft.name }}</span>
            </div>
            <span class="text-xs px-2 py-0.5 rounded-full font-medium bg-accent-500/10 text-accent-600 ring-1 ring-accent-500/30 shrink-0">
              {{ surveyView.scaleLabel }}
            </span>
          </div>
          <p class="text-surface-700 mb-3">
            <span class="text-surface-400 text-xs">{{ t("ai_cards.publish_survey.question") }}:</span>
            {{ surveyView.draft.question }}
          </p>

          <button
            v-if="canWrite"
            class="btn-primary text-sm inline-flex items-center gap-1.5"
            @click="emit('publish-survey', { draft: surveyView.draft! })"
          >
            <CheckCircle2 class="size-4" /> {{ t("ai_cards.publish_survey.apply") }}
          </button>
          <p v-else class="text-xs text-surface-400">{{ t("ai_cards.publish_survey.no_access") }}</p>
        </template>
        <p v-else class="text-xs text-warning-600 flex items-center gap-1">
          <AlertTriangle class="size-3.5" /> {{ t("ai_cards.publish_survey.invalid") }}
        </p>
      </template>

      <!-- Copilot Apply: draft_broadcast (PROPOSAL — human opens the wizard prefilled) -->
      <template v-else-if="call.tool === 'draft_broadcast'">
        <div class="flex items-center justify-between gap-2 mb-2">
          <div class="flex items-center gap-2 min-w-0">
            <Megaphone class="size-5 text-accent-600 shrink-0" />
            <span class="font-semibold text-surface-900 truncate">{{ broadcastDraft.name || t("ai_cards.draft_broadcast.untitled") }}</span>
          </div>
          <span
            v-if="broadcastDraft.filter"
            class="text-xs px-2 py-0.5 rounded-full font-medium shrink-0"
            :class="confClass(broadcastDraft.confidence)"
            :title="t('ai_cards.draft_broadcast.confidence')"
          >{{ confLabel(broadcastDraft.confidence) }}</span>
        </div>

        <!-- Audience (when the NL filter was built) -->
        <div v-if="broadcastDraft.filter" class="flex items-baseline gap-2 mb-2">
          <Users class="size-4 text-accent-600" />
          <span class="text-2xl font-bold text-surface-900">{{ broadcastDraft.audienceSize ?? "—" }}</span>
          <span class="text-xs text-surface-500">{{ t("ai_cards.draft_broadcast.recipients") }}</span>
        </div>

        <!-- Suggested message text — readable block, operator copies into the chosen channel/template. -->
        <div v-if="broadcastDraft.bodyText" class="mb-3">
          <div class="text-xs text-surface-400 mb-1">{{ t("ai_cards.draft_broadcast.body") }}</div>
          <p class="text-surface-700 whitespace-pre-wrap rounded bg-surface-50 p-2 text-sm">{{ broadcastDraft.bodyText }}</p>
        </div>

        <!-- NL failure: no filter → show the friendly message, still let the operator copy the text. -->
        <p v-if="broadcastDraft.error" class="text-xs text-warning-600 flex items-start gap-1 mb-2">
          <AlertTriangle class="size-3.5 mt-0.5 shrink-0" />
          {{ broadcastDraft.message || t("ai_cards.draft_broadcast.invalid") }}
        </p>

        <button
          v-if="broadcastDraft.filter && canWrite"
          class="btn-primary text-sm inline-flex items-center gap-1.5"
          @click="emit('draft-broadcast', { filter: broadcastDraft.filter!, suggestedName: broadcastDraft.name, bodyText: broadcastDraft.bodyText })"
        >
          <Send class="size-4" /> {{ t("ai_cards.draft_broadcast.apply") }}
        </button>
        <p v-else-if="broadcastDraft.filter && !canWrite" class="text-xs text-surface-400">
          {{ t("ai_cards.draft_broadcast.no_access") }}
        </p>
      </template>

      <!-- Copilot Apply: create_experience (PROPOSAL — human opens the web-tool editor prefilled) -->
      <template v-else-if="call.tool === 'create_experience'">
        <template v-if="experienceDraft.draft">
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-center gap-2 min-w-0">
              <MousePointerClick class="size-5 text-accent-600 shrink-0" />
              <span class="font-semibold text-surface-900 truncate">{{ experienceDraft.draft.title }}</span>
            </div>
            <span class="text-xs px-2 py-0.5 rounded-full font-medium bg-accent-500/10 text-accent-600 ring-1 ring-accent-500/30 shrink-0">
              {{ experienceDraft.draft.kind === 'survey' ? t("ai_cards.create_experience.kind_survey") : t("ai_cards.create_experience.kind_popup") }}
            </span>
          </div>
          <p v-if="experienceDraft.snippet" class="text-surface-700 mb-2">{{ experienceDraft.snippet }}</p>
          <div v-if="experienceDraft.draft.trigger.event_type" class="text-xs text-surface-600 mb-3">
            <span class="text-surface-400">{{ t("ai_cards.create_experience.trigger") }}:</span>
            <code class="ml-1 text-surface-700">{{ experienceDraft.draft.trigger.event_type }}</code>
          </div>

          <button
            v-if="canWrite"
            class="btn-primary text-sm inline-flex items-center gap-1.5"
            @click="emit('create-experience', { draft: experienceDraft.draft! })"
          >
            <Plus class="size-4" /> {{ t("ai_cards.create_experience.apply") }}
          </button>
          <p v-else class="text-xs text-surface-400">{{ t("ai_cards.create_experience.no_access") }}</p>
        </template>
        <p v-else class="text-xs text-warning-600 flex items-center gap-1">
          <AlertTriangle class="size-3.5" /> {{ out.error ? out.error : t("ai_cards.create_experience.invalid") }}
        </p>
      </template>

      <!-- Copilot Apply: explain_decision (READ — plain-text explanation, no Apply) -->
      <template v-else-if="call.tool === 'explain_decision'">
        <p v-if="explainView.text" class="text-surface-700 whitespace-pre-wrap leading-relaxed">{{ explainView.text }}</p>
        <p v-else class="text-xs text-warning-600 flex items-center gap-1">
          <AlertTriangle class="size-3.5" /> {{ explainView.error || t("ai_cards.explain_decision.empty") }}
        </p>
      </template>

      <!-- Phase-1 READ: list_surveys (no write — optional «Открыть» to the report) -->
      <template v-else-if="call.tool === 'list_surveys'">
        <ul v-if="surveyListView.rows.length" class="divide-y divide-surface-100">
          <li
            v-for="s in surveyListView.rows"
            :key="s.id"
            class="py-1.5 flex items-center justify-between gap-2"
          >
            <button
              class="text-left text-accent-700 hover:underline inline-flex items-center gap-1 truncate min-w-0"
              @click="emit('open-survey', s.id)"
            >
              <span class="truncate">{{ s.name }}</span>
              <ExternalLink class="size-3 shrink-0" />
            </button>
            <div class="flex items-center gap-2 shrink-0">
              <span class="text-xs text-surface-500">
                {{ s.responsesCount }} {{ t("ai_cards.list_surveys.responses") }}
              </span>
              <span
                class="text-[11px] px-1.5 py-0.5 rounded-full font-medium"
                :class="surveyStatusClass(s.status)"
              >{{ surveyStatusLabel(s.status) }}</span>
            </div>
          </li>
        </ul>
        <p v-else class="text-surface-400 text-xs">{{ t("ai_cards.list_surveys.none") }}</p>
      </template>

      <!-- Phase-1 READ: list_clients (FULL PII shown to the operator — intended) -->
      <template v-else-if="call.tool === 'list_clients'">
        <div v-if="clientListView.rows.length" class="overflow-x-auto">
          <table class="w-full text-xs">
            <thead>
              <tr class="text-surface-400 text-left">
                <th class="font-medium pb-1 pr-2">{{ t("ai_cards.list_clients.col_name") }}</th>
                <th class="font-medium pb-1 pr-2">{{ t("ai_cards.list_clients.col_contacts") }}</th>
                <th class="font-medium pb-1 pr-2">{{ t("ai_cards.list_clients.col_stage") }}</th>
                <th class="font-medium pb-1">{{ t("ai_cards.list_clients.col_region") }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-surface-100">
              <tr v-for="c in clientListView.rows" :key="c.id" class="align-top">
                <td class="py-1.5 pr-2">
                  <button
                    class="text-accent-700 hover:underline inline-flex items-center gap-1 text-left"
                    @click="emit('open-profile', c.id)"
                  >
                    <span class="truncate max-w-[12rem]">{{ c.displayName || t("ai_cards.list_clients.no_name") }}</span>
                    <ExternalLink class="size-3 shrink-0" />
                  </button>
                </td>
                <td class="py-1.5 pr-2 text-surface-700">
                  <div v-if="c.email" class="truncate max-w-[14rem]">{{ c.email }}</div>
                  <div v-if="c.phone" class="text-surface-500 tabular-nums">{{ c.phone }}</div>
                  <span v-if="!c.email && !c.phone" class="text-surface-300">—</span>
                </td>
                <td class="py-1.5 pr-2 text-surface-600">{{ c.lifecycleStage || "—" }}</td>
                <td class="py-1.5 text-surface-600">{{ c.region || "—" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-surface-400 text-xs">{{ t("ai_cards.list_clients.none") }}</p>
      </template>

      <!-- Phase-1 READ: recommend_offer_by_segment (NBO over a segment sample, no write) -->
      <template v-else-if="call.tool === 'recommend_offer_by_segment'">
        <template v-if="segmentOffersView.rows.length">
          <div v-if="segmentOffersView.sampled !== null" class="text-xs text-surface-400 mb-2">
            {{ t("ai_cards.recommend_offer_by_segment.sampled") }}:
            <span class="font-medium text-surface-600">{{ segmentOffersView.sampled }}</span>
          </div>
          <ul class="space-y-2">
            <li
              v-for="o in segmentOffersView.rows"
              :key="o.offerId"
              class="rounded border border-surface-200 bg-surface-50/60 p-2"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="flex items-center gap-2 min-w-0">
                  <Gift class="size-4 text-accent-600 shrink-0" />
                  <span class="font-medium text-surface-900 truncate">{{ o.offerName }}</span>
                </div>
                <div class="flex items-center gap-1.5 shrink-0">
                  <span
                    v-if="o.shareOfSegment !== null"
                    class="text-[11px] px-1.5 py-0.5 rounded-full font-medium bg-accent-500/10 text-accent-600 ring-1 ring-accent-500/30"
                    :title="t('ai_cards.recommend_offer_by_segment.share')"
                  >{{ fracPct(o.shareOfSegment) }}</span>
                  <span
                    v-if="o.avgScore !== null"
                    class="text-[11px] px-1.5 py-0.5 rounded-full font-medium bg-success-500/10 text-success-600 ring-1 ring-success-500/30"
                    :title="t('ai_cards.recommend_offer_by_segment.avg_score')"
                  >{{ fracPct(o.avgScore) }}</span>
                </div>
              </div>
              <p v-if="o.rationale" class="text-surface-600 text-xs mt-1">{{ o.rationale }}</p>
            </li>
          </ul>
        </template>
        <p v-else class="text-surface-400 text-xs">{{ t("ai_cards.recommend_offer_by_segment.none") }}</p>
      </template>

      <!-- search_docs (Phase 2 RAG): clickable help sections -->
      <template v-else-if="call.tool === 'search_docs'">
        <div v-if="out.unavailable" class="text-surface-500">{{ out.message }}</div>
        <template v-else-if="Array.isArray(out.rows) && out.rows.length">
          <ul class="space-y-2">
            <li
              v-for="(r, i) in out.rows"
              :key="r.url || i"
              class="rounded border border-surface-200 bg-surface-50/60 p-2"
            >
              <div class="flex items-start justify-between gap-2">
                <component
                  :is="r.url ? 'router-link' : 'span'"
                  :to="r.url || undefined"
                  class="flex items-center gap-1.5 min-w-0"
                  :class="r.url ? 'text-accent-600 hover:underline' : 'text-surface-800'"
                >
                  <BookOpen class="size-4 shrink-0" />
                  <span class="font-medium truncate">{{ r.title }}</span>
                  <ExternalLink v-if="r.url" class="size-3 shrink-0 opacity-60" />
                </component>
                <span v-if="r.section" class="text-[11px] px-1.5 py-0.5 rounded-full font-medium bg-surface-200/70 text-surface-600 shrink-0">{{ r.section }}</span>
              </div>
              <p v-if="r.snippet" class="text-surface-600 text-xs mt-1 line-clamp-2">{{ r.snippet }}</p>
            </li>
          </ul>
        </template>
        <p v-else class="text-surface-400 text-xs">{{ out.note || t("ai_cards.search_docs.none") }}</p>
      </template>

      <!-- Unknown tool → fallback to JSON (forward-compat) -->
      <template v-else>
        <pre class="bg-surface-50 rounded p-2 overflow-x-auto text-xs max-h-[200px]">{{ jsonPretty(call.output) }}</pre>
      </template>

      <!-- Raw JSON (always available) -->
      <details class="mt-2" :open="rawOpen">
        <summary
          class="text-xs text-surface-400 hover:text-surface-600 cursor-pointer inline-flex items-center gap-1 select-none"
          @click.prevent="emit('toggle-raw')"
        >
          <component :is="rawOpen ? ChevronDown : ChevronRight" class="size-3.5" />
          Показать данные (JSON)
        </summary>
        <div v-if="rawOpen" class="mt-1.5 space-y-1.5">
          <div class="text-xs text-surface-500">Запрос инструмента «{{ call.tool }}»:</div>
          <pre class="bg-surface-50 rounded p-2 overflow-x-auto text-xs">{{ jsonPretty(call.input) }}</pre>
          <div class="text-xs text-surface-500">Результат:</div>
          <pre class="bg-surface-50 rounded p-2 overflow-x-auto text-xs max-h-[200px]">{{ jsonPretty(call.output) }}</pre>
        </div>
      </details>
    </div>
  </div>
</template>
